// src/components/padrekko/qc/MandatoryReasonModal.jsx
//
// Shared mandatory-rationale modal. Extracted from three byte-near-identical
// reason-capture modals — SendBackModal, CustomerSendBackModal, and the inline
// remove-reason modal in QcFindingCard — and reused for the kickback flow.
//
// Owns the whole shell: overlay + card, a required reason textarea with
// min-length validation, inline error, and Cancel/Submit with an internal
// submitting state. Consumers supply copy (title/icon/labels/placeholder), the
// async onSubmit(reason) -> { ok, error? }, and optional extra body content via
// `children` (SendBackModal's dispute-finding checkboxes render there).
//
// The submitting state is local: onSubmit returns a result object and the modal
// drives its own spinner from the awaited promise. Stores still set their own
// inflight flags for double-submit guards / optimistic updates, but the modal
// doesn't depend on them.

import { useState } from "react";
import { Loader2, X as XIcon, AlertCircle } from "lucide-react";

// Per-tone classes. amber = workflow send-backs (default); rose = destructive
// removal. Keeps the submit button, focus ring, and icon color in step.
const TONES = {
  amber: {
    icon: "text-amber-400",
    focus: "focus:border-amber-400",
    submit: "bg-amber-500/15 border-amber-500/50 text-amber-200 hover:bg-amber-500/25",
  },
  rose: {
    icon: "text-rose-300",
    focus: "focus:border-rose-400",
    submit: "bg-rose-600 border-rose-600 text-white hover:bg-rose-500",
  },
};

export default function MandatoryReasonModal({
  isOpen,
  onClose,
  title,
  icon = null,
  description = null,
  label = "Reason",
  placeholder = "",
  submitLabel = "Submit",
  submittingLabel = "Submitting…",
  minLength = 1,
  tone = "amber",
  onSubmit,
  children = null,
}) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const t = TONES[tone] ?? TONES.amber;
  const trimmed = reason.trim();
  const valid = trimmed.length >= minLength;

  function reset() {
    setReason("");
    setError(null);
    setSubmitting(false);
  }
  function handleCancel() {
    if (submitting) return;
    reset();
    onClose();
  }
  async function handleSubmit() {
    if (!valid || submitting) {
      if (!valid) {
        setError(
          minLength > 1
            ? `Please enter at least ${minLength} characters.`
            : "Reason is required."
        );
      }
      return;
    }
    setSubmitting(true);
    setError(null);
    const result = await onSubmit(trimmed);
    if (result?.ok) {
      reset();
      onClose();
    } else {
      setSubmitting(false);
      setError(result?.error || "Something went wrong.");
    }
  }

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={(e) => e.target === e.currentTarget && handleCancel()}
    >
      <div className="w-full max-w-md rounded-xl border border-slate-700 bg-bg-soft shadow-2xl">
        <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-100 flex items-center gap-1.5">
            {icon && <span className={t.icon}>{icon}</span>}
            {title}
          </h3>
          <button
            type="button"
            onClick={handleCancel}
            disabled={submitting}
            className="text-fg-secondary hover:text-slate-200 transition disabled:opacity-40"
            title="Cancel"
          >
            <XIcon size={14} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {description && (
            <p className="text-[11px] text-slate-300 leading-relaxed">{description}</p>
          )}

          <div className="space-y-1.5">
            <label className="text-[10px] font-mono uppercase tracking-widest text-fg-secondary">
              {label} <span className="text-red-300">*</span>
            </label>
            <textarea
              autoFocus
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (error) setError(null);
              }}
              disabled={submitting}
              rows={3}
              placeholder={placeholder}
              className={`w-full text-[11px] px-2.5 py-2 rounded border border-slate-700
                          bg-slate-900 text-slate-100 placeholder:text-slate-600
                          focus:outline-none ${t.focus}
                          disabled:opacity-50 leading-snug resize-none`}
            />
          </div>

          {children}

          {error && (
            <div className="flex items-start gap-1.5 text-[10px] text-red-300 leading-snug">
              <AlertCircle size={10} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-800 flex justify-end gap-1.5">
          <button
            type="button"
            onClick={handleCancel}
            disabled={submitting}
            className="text-[11px] px-3 py-1.5 rounded border border-slate-700 text-slate-300
                       hover:bg-slate-800 hover:border-slate-600 transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!valid || submitting}
            className={`flex items-center gap-1 text-[11px] px-3 py-1.5 rounded border
                        ${t.submit} disabled:opacity-40 disabled:cursor-not-allowed transition`}
          >
            {submitting && <Loader2 size={11} className="animate-spin" />}
            {submitting ? submittingLabel : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
